import React from 'react'
import { ChartLineUpIcon } from '@phosphor-icons/react'
import { ScoopSpinner } from '../ui'
import { SummaryChart } from './SummaryChart'
import { SummaryTable } from './SummaryTable'
import { SummaryKPI } from './SummaryKPI'
import styles from './Summary.module.scss'

/**
 * Pure presentational component for rendering summaries.
 *
 * IMPORTANT: This component does NOT consume any context.
 * All data must be passed via props.
 *
 * This allows Summary to be used in:
 * - Explorer (with ExplorerContext via ExplorerSummary bridge)
 * - Chat (with AI-generated config via useSummaryFromConfig)
 * - Canvas (with saved insights via useSummaryFromConfig)
 * - Slack embeds (with URL config via useSummaryFromConfig)
 *
 * @param {Object} props
 * @param {Object} props.chartOptions - Pre-built ECharts options for chart view
 * @param {Object} props.tableData - Data for table view
 * @param {Object} props.kpiData - Data for KPI view
 * @param {string} props.view - View type: 'chart' | 'table' | 'kpi'
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Error} props.error - Error object if data fetch failed
 * @param {boolean} props.hasData - Whether there is data to display
 * @param {number|string} props.height - Chart height
 * @param {number|string} props.width - Chart width
 * @param {boolean} props.clickable - Whether chart elements are clickable
 * @param {Function} props.onChartClick - Callback for chart element clicks
 * @param {Function} props.onDrill - Callback for drill-down actions
 * @param {Object} props.embeddedSizeProps - Size props for embedded rendering
 * @param {boolean} props.chat - Whether rendering in chat context
 * @param {boolean} props.slack - Whether rendering for Slack
 */
export const Summary = ({
    // Data
    chartOptions,
    tableData,
    kpiData,
    view = 'chart',

    // Status
    isLoading = false,
    error = null,
    hasData = true,

    // Display options
    height,
    width,
    clickable = false,
    onChartClick,
    onDrill,

    // Embedded sizing
    embeddedSizeProps,

    // Feature flags
    chat = false,
    slack = false,
}) => {
    // Loading state
    if (isLoading) {
        return (
            <div className={styles.centerContent}>
                <ScoopSpinner size={40} />
            </div>
        )
    }

    // Error state
    if (error) {
        return (
            <div className={styles.centerContent}>
                <span className={styles.error}>
                    Error loading data: {error.message || 'Unknown error'}
                </span>
            </div>
        )
    }

    // Empty state - no data to display
    if (!hasData) {
        return (
            <div className={styles.centerContent}>
                <div className={styles.emptyState}>
                    <ChartLineUpIcon size={48} className={styles.emptyStateIcon} />
                    <span className={styles.emptyStateText}>
                        Select metrics from the Data tab to visualize your data
                    </span>
                </div>
            </div>
        )
    }

    // Render appropriate view
    const renderContent = () => {
        switch (view) {
            case 'chart':
                return chartOptions ? (
                    <SummaryChart
                        options={chartOptions}
                        height={height}
                        width={width}
                        clickable={clickable}
                        onChartClick={onChartClick}
                        onDrill={onDrill}
                        embeddedSizeProps={embeddedSizeProps}
                        chat={chat}
                    />
                ) : null

            case 'table':
                return <SummaryTable data={tableData} />

            case 'kpi':
                return <SummaryKPI data={kpiData} />

            default:
                return null
        }
    }

    return <div className={styles.container}>{renderContent()}</div>
}
